'use client'

import Collapse from '@mui/material/Collapse'
import Grid2 from '@mui/material/Grid2'
import { ReactElement, useRef, useState } from 'react'
import { ItemWithId, ListRenderItem } from '../FlatList/types'
import useIsomorphicLayoutEffect from '../useLayoutEffect'
import useWindowSize from '../useWindowSize'
import ActionButton from './ActionButton'

const { useLayoutEffect } = useIsomorphicLayoutEffect

export type CollapsibleGridProps<Item extends ItemWithId> = {
    data: ReadonlyArray<Item> | null
    renderItem: ListRenderItem<Item>
    visibleRows: 1 | 2
    cols: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
}

const GRID_SPACING = 3

export default function CollapsibleGrid<Item extends ItemWithId>({
    data,
    renderItem,
    visibleRows,
    cols,
}: CollapsibleGridProps<Item>): ReactElement | null {
    const [isExpanded, setExpanded] = useState(false)
    const [collapsedHeight, setCollapsedHeight] = useState<number>(0)
    const { width: windowWidth } = useWindowSize()

    // Track the rendered height of the first item in each row so we know what height to use for <Collapse>
    const row1Ref = useRef<HTMLDivElement>(null)
    const row2Ref = useRef<HTMLDivElement>(null)
    useLayoutEffect(() => {
        const node1 = row1Ref.current
        const node2 = row2Ref.current

        // Only check if at least one div is rendered
        if (!node1 && !node2) {
            return
        }

        // We need `rAF` otherwise the height can be measured too early and the value will be too small
        requestAnimationFrame(() => {
            const height1 = node1 ? node1.getBoundingClientRect().height : 0
            const height2 = node2 ? node2.getBoundingClientRect().height : 0
            let newHeight = 0

            if (visibleRows === 1) {
                newHeight = height1
            } else {
                // Height of all items combined
                newHeight = height1 + height2
            }

            if (newHeight > 0) {
                setCollapsedHeight(newHeight + GRID_SPACING * 8)
            }
        })
    }, [visibleRows, cols, isExpanded, windowWidth])

    if (!data || data.length === 0) {
        return null
    }

    const shouldShowButton = data.length > visibleRows * cols

    return (
        <>
            <Collapse in={isExpanded} collapsedSize={collapsedHeight} timeout="auto">
                <Grid2 container spacing={GRID_SPACING} sx={{ mb: isExpanded ? 3 : undefined }}>
                    {data.map((item, index) => {
                        // Add a ref to the first item in each row
                        let ref = null
                        if (index === 0) {
                            ref = row1Ref
                        } else if (visibleRows === 2 && index === cols + 1) {
                            ref = row2Ref
                        }

                        return (
                            <Grid2 size={{ xs: 12 / cols }} key={index} ref={ref}>
                                {renderItem({ item, index })}
                            </Grid2>
                        )
                    })}
                </Grid2>
            </Collapse>
            {shouldShowButton ? (
                <ActionButton actionText="Show" isExpanded={isExpanded} setExpanded={setExpanded} />
            ) : null}
        </>
    )
}
